import React, { useEffect, useState, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setAccessToken } from '@adthlete/shared-frontend'
import { fetchCurrentUser } from '../redux/userSlice'
import api from '../lib/api'
import { AppDispatch } from '../store'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { CheckCircle, XCircle } from 'lucide-react'

const useAppDispatch = () => useDispatch<AppDispatch>()

const REDIRECT_DELAY_MS = 3000

const EmailConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying')
  const [message, setMessage] = useState('Verifying your email address...')
  const hasVerified = useRef(false)
  const [landingPath, setLandingPath] = useState('/')

  // Step 1: Verify the email token
  useEffect(() => {
    const token = searchParams.get('token')

    if (!token) {
      setStatus('error')
      setMessage('No confirmation token found. Please check the link or try again.')
      return
    }

    if (hasVerified.current) {
      return
    }
    hasVerified.current = true

    const verifyEmail = async () => {
      try {
        // Called directly rather than through confirmEmailThunk to read threadId: the message a
        // brand wrote from a talent sheet before signing up, sent now that they've confirmed
        const { data } = await api.post('/users/confirm-email', { token })
        if (data?.token) setAccessToken(data.token)
        await dispatch(fetchCurrentUser()).unwrap()
        setLandingPath(data?.threadId ? `/messages/${data.threadId}` : '/')
        setStatus('success')
        setMessage('Your email has been successfully verified!')
      } catch (err: any) {
        setStatus('error')
        setMessage(
          err?.response?.data?.error ||
            (typeof err === 'string' ? err : 'An error occurred during email verification.'),
        )
      }
    }

    verifyEmail()
  }, [searchParams, dispatch])

  // Step 2: Redirect ONLY after success status is set, with a guaranteed delay
  useEffect(() => {
    if (status !== 'success') return

    const timer = setTimeout(() => {
      navigate(landingPath)
    }, REDIRECT_DELAY_MS)

    return () => clearTimeout(timer)
  }, [status, navigate, landingPath])

  return (
    <div className="flex min-h-screen items-center justify-center py-8 relative">
      <div className="fixed inset-0 -z-10 bg-[#F4F7EF]"></div>

      <div className="w-full max-w-md px-4">
        <Card className="rounded-[28px] border-0 bg-[#FFFFFF] py-4 shadow-[0_20px_60px_rgba(16,24,40,0.08)] sm:px-4">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {status === 'verifying' && (
                <img
                  src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                  alt="Athios Logo"
                  className="w-10 h-10 animate-pulse"
                />
              )}
              {status === 'success' && <CheckCircle className="w-16 h-16 text-green-600" />}
              {status === 'error' && <XCircle className="w-16 h-16 text-red-600" />}
            </div>
            <CardTitle className="text-3xl font-black text-[#0F172A] tracking-tight">
              {status === 'verifying' && 'Verifying Email'}
              {status === 'success' && 'Welcome to Athios!'}
              {status === 'error' && 'Verification Failed'}
            </CardTitle>
            <CardDescription className="mt-2 text-[#4B5563]">{message}</CardDescription>
          </CardHeader>
          {status === 'success' && (
            <CardContent>
              <p className="text-center text-sm text-[#374151] mb-4">
                Redirecting you to the dashboard...
              </p>
              {/* Animated progress bar */}
              <div className="w-full bg-[#F3F4F6] rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-[#C0FD72] h-1.5 rounded-full"
                  style={{
                    animation: `progressBar ${REDIRECT_DELAY_MS}ms linear forwards`,
                  }}
                />
              </div>
              <style>{`
                @keyframes progressBar {
                  from { width: 0%; }
                  to { width: 100%; }
                }
              `}</style>
            </CardContent>
          )}
          {status === 'error' && (
            <CardContent>
              <Button
                onClick={() => navigate('/signin')}
                className="w-full bg-[#C0FD72] hover:bg-[#a8e45a] text-black font-medium"
              >
                Go to Sign In
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  )
}

export default EmailConfirmation
